import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * SHEET — a panel that slides in from an edge.
 *
 * Distinct from Dialog: a dialog interrupts and demands an answer, a sheet
 * shows more detail about something already on screen. The job-details panel
 * and filter drawers are sheets; "are you sure?" is a dialog.
 *
 * Note Dialog already becomes a bottom sheet on phones. This is for the case
 * where a side panel is wanted on desktop too.
 */
export const sheetRecipe = cva(
  [
    uiMarker,
    'fixed z-50 flex flex-col gap-4 bg-card text-card-foreground shadow-e3',
    'transition ease-in-out',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:duration-slow data-[state=open]:duration-slow',
  ],
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b border-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 rounded-t-xl border-t border-border data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        right:
          'inset-y-0 right-0 h-full border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      },
      size: {
        sm: '',
        md: '',
        lg: '',
        full: '',
      },
    },
    compoundVariants: [
      // Width applies to side panels, height to top/bottom ones. Side panels
      // stay full-width on phones — a 400px drawer on a 390px screen is silly.
      { side: 'left', size: 'sm', class: 'w-full sm:max-w-sm' },
      { side: 'left', size: 'md', class: 'w-full sm:max-w-md' },
      { side: 'left', size: 'lg', class: 'w-full sm:max-w-2xl' },
      { side: 'left', size: 'full', class: 'w-full' },
      { side: 'right', size: 'sm', class: 'w-full sm:max-w-sm' },
      { side: 'right', size: 'md', class: 'w-full sm:max-w-md' },
      { side: 'right', size: 'lg', class: 'w-full sm:max-w-2xl' },
      { side: 'right', size: 'full', class: 'w-full' },
      { side: 'top', size: 'sm', class: 'max-h-[30vh]' },
      { side: 'top', size: 'md', class: 'max-h-[50vh]' },
      { side: 'top', size: 'lg', class: 'max-h-[80vh]' },
      { side: 'top', size: 'full', class: 'h-full' },
      { side: 'bottom', size: 'sm', class: 'max-h-[30vh]' },
      { side: 'bottom', size: 'md', class: 'max-h-[50vh]' },
      { side: 'bottom', size: 'lg', class: 'max-h-[80vh]' },
      { side: 'bottom', size: 'full', class: 'h-full' },
    ],
    defaultVariants: { side: 'right', size: 'md' },
  }
);

export type SheetRecipeProps = VariantProps<typeof sheetRecipe>;

export const sheetHeaderRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex items-start justify-between gap-4 border-b border-border p-6 pb-4',
]);
export const sheetBodyRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'min-h-0 flex-1 overflow-y-auto p-6 pt-0',
]);
export const sheetFooterRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col-reverse gap-2 border-t border-border p-6 pt-4 sm:flex-row sm:justify-end',
]);

/**
 * BOTTOM SHEET — the phone's panel: it rises from the bottom edge and a
 * downward drag puts it away.
 *
 * Not a size of Sheet. Sheet is a Radix dialog that animates on CSS, and a
 * drag needs the panel to follow the finger, which is vaul's job — so the two
 * cannot share a root. The regions below are drawn to the mobile page
 * structures in Figma: 16px insets, no rule under the header, and a footer of
 * equal-width buttons.
 *
 * vaul moves the panel with inline transforms, so nothing here carries the
 * `animate-in` classes the other overlays use; the two fight over `transform`
 * and the panel stutters on open.
 */
export const bottomSheetRecipe = cva(
  [
    uiMarker,
    'fixed inset-x-0 bottom-0 z-50 flex flex-col outline-none',
    'rounded-t-3xl border-t border-border bg-card text-card-foreground shadow-e3',
    // The home indicator on an iPhone sits over the last 34px of the screen;
    // without this a footer's buttons sit underneath it.
    'pb-[env(safe-area-inset-bottom)]',
    // A tablet or desktop gets a phone-width panel rather than a strip the
    // width of the monitor.
    'sm:mx-auto sm:max-w-lg',
  ],
  {
    variants: {
      /**
       * `auto` is as tall as its content, which is what "the panel stays small
       * if the content fits" asks for; `full` is the whole screen for a form.
       * Both stop 4rem short of the top, so the status pill above stays in
       * sight and the page underneath still reads as the page underneath.
       */
      size: {
        auto: 'max-h-[calc(100dvh-4rem)]',
        full: 'h-[calc(100dvh-4rem)]',
      },
    },
    defaultVariants: { size: 'auto' },
  }
);

export type BottomSheetRecipeProps = VariantProps<typeof bottomSheetRecipe>;

/**
 * No fade classes, for the reason on the panel: vaul fades the backdrop
 * itself, in step with the drag.
 */
export const bottomSheetBackdropRecipe = cva([
  uiMarker,
  'fixed inset-0 z-50 bg-foreground/40',
]);

export const bottomSheetHeaderRecipe = cva(
  [uiMarker, uiMarkerLayout, 'shrink-0 gap-3 p-4'],
  {
    variants: {
      /**
       * `standard` is eyebrow and title on the left, close on the right.
       * `centered` is the iOS arrangement for a creation surface: close on
       * the left, the title in the middle, the confirm on the right.
       */
      layout: {
        standard: 'flex items-start justify-between',
        centered: 'grid grid-cols-[auto_1fr_auto] items-center text-center',
      },
    },
    defaultVariants: { layout: 'standard' },
  }
);

export const bottomSheetBodyRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'min-h-0 flex-1 overflow-y-auto px-4 pb-4',
]);

/** Cancel and Create side by side at equal width, as the Figma draws them. */
export const bottomSheetFooterRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'grid shrink-0 auto-cols-fr grid-flow-col gap-3 border-t border-border p-4',
]);

/**
 * OPTION SHEET — a select whose options rise in a bottom sheet.
 *
 * Its own recipes rather than Command's: Command is drawn for a popover under
 * a desktop field — 14px rows, a 300px cap, the search box on top — and the
 * phone wants the reverse of each. Rows at the body-large size a thumb can
 * hit, the whole sheet for the list, and the search down where the thumb is.
 */
export const optionSheetItemRecipe = cva([
  uiMarker,
  // Pulled out by its own inset so the text lines up with the title while
  // the highlight still has room either side of it.
  '-mx-2 flex min-h-11 cursor-pointer select-none items-center gap-2 rounded-md px-2',
  'font-sans text-body-lg outline-none',
  // cmdk drives the highlight from data-selected, not :hover, so the keyboard
  // and the pointer land on the same styling.
  'data-[selected=true]:bg-muted',
  'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
]);

/** The floating search pill along the bottom of a long list. */
export const optionSheetSearchRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'sticky bottom-0 mx-auto mt-2 flex h-control-xl w-full max-w-xs items-center gap-2',
  'rounded-pill bg-muted px-4 text-muted-foreground shadow-e2',
]);

export const optionSheetSearchInputRecipe = cva([
  uiMarker,
  'h-full w-full bg-transparent font-sans text-body-lg text-foreground outline-none',
  'placeholder:text-muted-foreground',
]);

/**
 * AUTOCOMPLETE / COMBOBOX
 *
 * A text input that suggests as you type. Presentation only — where the
 * suggestions come from is the caller's business, which is what keeps this out
 * of the design system's dependency graph.
 */
export const autocompleteListRecipe = cva([
  uiMarker,
  'absolute inset-x-0 top-full z-50 mt-1 max-h-64 overflow-y-auto',
  'rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-e3',
]);

export const autocompleteItemRecipe = cva(
  [
    uiMarker,
    'flex w-full cursor-default select-none flex-col items-start gap-0.5',
    'rounded-sm px-2 py-2 text-left font-sans outline-none',
  ],
  {
    variants: {
      active: {
        true: 'bg-accent text-accent-foreground',
        false: '',
      },
    },
    defaultVariants: { active: false },
  }
);

export const autocompleteEmptyRecipe = cva([
  uiMarker,
  'px-2 py-3 text-center font-sans text-body-sm text-muted-foreground',
]);
